import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle2,
  Folder,
  Fullscreen,
  MessageSquare,
  Settings,
  Sparkles,
  Tag,
  User,
  X,
  Zap,
} from 'lucide-react';
import { marked } from 'marked';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { Button } from '../../../components/ui/button.js';
import { FusionRequestSheet } from '../../scenario/components/FusionRequestSheet.js';
import { ScenarioAiSummarySheet } from '../../scenario/components/ScenarioAiSummarySheet.js';
import { ScenarioTagSelector } from '../../scenario/components/ScenarioTagSelector.js';
import {
  presetToDateRange,
  rollupByScenarioMetric,
  useEffectiveMainMetricId,
  useKpiValues,
} from '../../kpi/hooks.js';
import { ScenarioProcessDiagram } from '../../scenario/components/ScenarioProcessDiagram.js';
import { TagChip } from '../../scenario/components/TagChip.js';
import { useScenarioDetail, useScenariosList, useUpdateScenario } from '../../scenario/hooks.js';
import type { ScenarioRow } from '../../scenario/types.js';
import { formatDateTime, formatSchedule } from '../../scenario/utils.js';
import { usePersistentState } from '../../../lib/persistent-state.js';
import {
  PresentationDateRangeDropdown,
  type PresentationDateRangePreset,
} from '../components/PresentationDateRangeDropdown.js';
import { PresentationKpiTable } from '../components/PresentationKpiTable.js';
import { PresentationScenariosRail } from '../components/PresentationScenariosRail.js';

/**
 * Presentation slide for a single scenario. Two-column layout mirrors
 * the legacy `wf-audit/src/slices/presentation/components/presenationItem/Provider.tsx`:
 *
 *   [ Title · prev/next · AI Summary · Fusion Request · Fullscreen · Close ]
 *   ┌─────────────────────────┬────────────────────────────────────────┐
 *   │ Executive Summary       │                                        │
 *   │ Applications            │  Screenshot (large, lazy-loaded)       │
 *   │ Executions (pie chart)  │                                        │
 *   │ Info (metadata table)   │                                        │
 *   └─────────────────────────┴────────────────────────────────────────┘
 *
 * Deferred from legacy:
 *   - Sidebar drawer of all scenarios (a `↩ Back to list` button covers
 *     the navigation use case for now)
 *   - Date-range filter on Executions (couples to the scenario filter
 *     store; lands when the legacy filter store ports)
 *   - Per-scenario KPI metrics array on the Executions card — the
 *     engine response doesn't carry this yet (`metrics.kpiMetrics`
 *     is a wf-audit-engine-specific aggregation). Card shows the
 *     `lastSuccessfulRun*` counters from the scenario detail response
 *     for the same insight at lower fidelity.
 *
 * The toolbar's Fusion Request + AI Technical Summary buttons mirror
 * the row-level icons on the scenarios-list page — same components
 * (`FusionRequestSheet`, `ScenarioAiSummarySheet`).
 */
export function PresentationDetailPage(): JSX.Element {
  const { presentationId } = useParams<{ presentationId: string }>();
  const navigate = useNavigate();
  const { data: detail, isLoading, error } = useScenarioDetail(presentationId);
  const { data: scenarios } = useScenariosList();

  const [fusionRequestOpen, setFusionRequestOpen] = useState(false);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);
  // Date range preset for the executions / KPI rollup. Today this is
  // a visual control matching legacy parity; the data filtering wires
  // when `/v1/kpi-values` learns to accept a date range.
  const [dateRange, setDateRange] = useState<PresentationDateRangePreset>('last-30-days');
  // Collapse state for the left scenarios rail. Persisted across
  // reloads + scenario-switches so the operator's last layout
  // preference is restored on next visit. Mirrors the legacy
  // `wf-audit/.../presenationItem` sidebar toggle.
  const [railCollapsed, setRailCollapsed] = usePersistentState<'open' | 'closed'>(
    'wf-coe-app:presentation:rail-collapsed',
    'open',
    (raw): raw is 'open' | 'closed' => raw === 'open' || raw === 'closed',
  );
  const toggleRail = useCallback(() => {
    setRailCollapsed(railCollapsed === 'open' ? 'closed' : 'open');
  }, [railCollapsed, setRailCollapsed]);

  // Prev/next navigation walks the same scenarios list the operator
  // saw on the previous page. Wraps at edges so the operator can keep
  // pressing ← / → during a demo without dead-ending.
  const ids = useMemo(
    () => (scenarios ?? []).map((s) => String(s.fusionId ?? '')).filter(Boolean),
    [scenarios],
  );
  const currentIndex = presentationId ? ids.indexOf(presentationId) : -1;
  const prevId =
    ids.length > 0 && currentIndex >= 0 ? ids[(currentIndex - 1 + ids.length) % ids.length] : null;
  const nextId = ids.length > 0 && currentIndex >= 0 ? ids[(currentIndex + 1) % ids.length] : null;

  const goPrev = useCallback(() => {
    if (prevId) navigate(`/presentation/${prevId}`);
  }, [navigate, prevId]);
  const goNext = useCallback(() => {
    if (nextId) navigate(`/presentation/${nextId}`);
  }, [navigate, nextId]);

  // ← / → step through the filtered list while the page is open.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  function handleFullscreen(): void {
    if (!document.fullscreenElement) {
      void document.documentElement.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  }

  const name = (detail?.name as string | undefined) ?? `Scenario ${presentationId}`;
  const description = (detail?.description as string | undefined) ?? '';
  const folder = (detail?.folderName as string | undefined) ?? '';
  const totalModules = (detail?.totalModules as number | undefined) ?? 0;
  const screenshotLink = (detail?.screenshotLink as string | undefined) ?? '';
  const status = (detail?.status as string | undefined) ?? '';
  const tags = Array.isArray(detail?.tags)
    ? (detail?.tags as Array<{
        id?: number;
        title?: string;
        name?: string;
        color?: string | null;
        icon?: string | null;
      }>)
    : [];
  const applications = Array.isArray(detail?.applications)
    ? (detail?.applications as Array<{ name?: string; label?: string; type?: string }>)
    : [];
  const modifiedAt = (detail?.modifiedAt as string | undefined) ?? '';
  const modifiedBy = (detail?.modifiedBy as string | undefined) ?? '';
  const aiTechnicalSummary = (detail?.aiTechnicalSummary as string | undefined) ?? '';
  const hasNotifications = (detail?.hasNotifications as boolean | undefined) ?? false;
  const lastRunOperations = (detail?.lastSuccessfulRunOperations as number | undefined) ?? 0;

  const schedule = formatSchedule(
    (detail?.eventSchedule as Parameters<typeof formatSchedule>[0]) ?? undefined,
  );

  // Currently attached tag ids — derived from the detail response.
  // The toolbar's tag selector uses this as `currentTagIds` so the
  // popover's checkboxes pre-fill correctly.
  const currentTagIds = useMemo(
    () =>
      tags
        .map((t) => (t as { id?: number }).id)
        .filter((id): id is number => typeof id === 'number'),
    [tags],
  );

  // Per-tag remove. Backed by the same `PUT /v1/scenarios/{id}` write
  // the toolbar's tag selector uses — just calls it with the current
  // ids minus the one being removed. Fires-and-forgets: toasts (success
  // + error) are emitted by the hook, the query invalidation refreshes
  // the chip list, and the X-button hover state collapses with the
  // chip when the optimistic update lands.
  const { update: updateScenario } = useUpdateScenario(presentationId ?? '');
  const removeTag = useCallback(
    (tagId: number) => {
      if (!presentationId) return;
      const next = currentTagIds.filter((id) => id !== tagId);
      void updateScenario({ tagIds: next });
    },
    [presentationId, currentTagIds, updateScenario],
  );

  // KPI rollup for the active scenario — drives the right-column
  // KPI table. The active date-range preset (toolbar dropdown)
  // controls the lookback window; today's hook always queries the
  // license-scoped data and rolls up per-(scenario, metric).
  const { from: kpiFrom, to: kpiTo } = useMemo(() => presetToDateRange(dateRange), [dateRange]);
  const { data: kpiValues = [] } = useKpiValues({
    from: kpiFrom,
    to: kpiTo,
    aggregationType: 'day',
    fusionScenarioId: presentationId ?? undefined,
  });
  const mainMetricId = useEffectiveMainMetricId();
  const kpiTableRows = useMemo(() => {
    if (!presentationId) return null;
    const byMetric = rollupByScenarioMetric(kpiValues).get(presentationId);
    if (!byMetric || byMetric.size === 0) return null;
    // Prefer the license's main metric when it's present in the
    // rollup; otherwise sum across all metrics for this scenario so
    // the table populates in demo mode and on licenses that haven't
    // picked a main metric.
    let hoursSaved = 0;
    let value = 0;
    let count = 0;
    if (mainMetricId && byMetric.has(mainMetricId)) {
      const t = byMetric.get(mainMetricId)!;
      hoursSaved = t.quantity;
      value = t.value;
      count = t.count;
    } else {
      for (const t of byMetric.values()) {
        hoursSaved += t.quantity;
        value += t.value;
        count += t.count;
      }
    }
    const ops = Number(lastRunOperations) || 0;
    return [
      {
        scenarioName: name,
        hoursSaved,
        value,
        // Until the engine surfaces success / failure percentages
        // per scenario, show the rollup's `count` field as a rough
        // proxy: 100% when any value was recorded, else 0%.
        successfulExecutionsPct: count > 0 ? 100 : 0,
        operationsPct: ops > 0 ? 100 : 0,
      },
    ];
  }, [presentationId, mainMetricId, kpiValues, name, lastRunOperations]);

  return (
    // Explicit viewport-relative height. The IframeShell's outer is
    // `min-h-screen` (not `h-screen`), so `h-full` chained from here
    // resolves to whatever the content needs — which makes the rail's
    // long scenario list push the entire page taller and triggers
    // window-level scroll. Clamping the page to
    // `100 vh − chrome` (IframeShell top bar ~36 px + footer ~20 px)
    // forces the body's `min-h-0 overflow-hidden` to do its job and
    // the rail's `overflow-y-auto` to scroll internally.
    <div className="relative z-10 flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      {/* Top toolbar */}
      <header className="flex flex-wrap items-center gap-3 border-b border-foreground/10 bg-foreground/[0.02] px-6 py-3">
        <h1 className="min-w-0 flex-1 truncate text-xl font-light text-foreground" title={name}>
          {name}
        </h1>
        <PresentationDateRangeDropdown value={dateRange} onChange={setDateRange} />
        <div className="flex items-center gap-1 rounded-md border border-foreground/15">
          <button
            type="button"
            onClick={goPrev}
            disabled={!prevId}
            className="px-3 py-1.5 text-foreground/70 hover:bg-foreground/10 hover:text-foreground disabled:opacity-30"
            aria-label="Previous scenario"
            title="Previous (←)"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="h-5 w-px bg-foreground/15" />
          <button
            type="button"
            onClick={goNext}
            disabled={!nextId}
            className="px-3 py-1.5 text-foreground/70 hover:bg-foreground/10 hover:text-foreground disabled:opacity-30"
            aria-label="Next scenario"
            title="Next (→)"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {/* Tag-attach toolbar dropdown — same `ScenarioTagSelector`
            popover the scenario detail modal uses. Operator can flip
            tags on the current scenario without leaving the detail
            view, matching legacy parity. */}
        {presentationId ? (
          <ScenarioTagSelector fusionId={presentationId} currentTagIds={currentTagIds} />
        ) : null}
        {aiTechnicalSummary ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAiSummaryOpen(true)}
            className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
            title="Technical Steps"
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" /> AI Summary
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFusionRequestOpen(true)}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
          title="Make a Fusion Enhancement Request"
        >
          <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Fusion Request
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleFullscreen}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
          title="Fullscreen"
        >
          <Fullscreen className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => navigate('/presentation')}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
        >
          <X className="mr-1.5 h-3.5 w-3.5" /> Close
        </Button>
      </header>

      {error ? (
        <div className="mx-6 mt-4 rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
          {error.message}
        </div>
      ) : null}

      {/* Three-column body: scenarios rail | summary cards | screenshot.
          `min-h-0` is the flex-shrink-to-content escape hatch — without
          it the body's default `min-height: auto` lets child content
          push the row taller than its grandparent (the
          `h-full` flex column above), which prevented the rail's
          `overflow-y-auto` from kicking in and stretched the page
          vertically (operator feedback 2026-05-26). */}
      <div className="flex flex-1 min-h-0 flex-col overflow-y-auto md:flex-row md:items-stretch md:overflow-hidden">
        {/* Left rail — scenarios grouped by tag with Manage Tags
            entry-point on top. Replaces the previous "back to list"
            button as the primary cross-scenario navigation. */}
        <PresentationScenariosRail
          // Engine returns `Row[]` (loose typing in `useScenariosList`);
          // narrow to the `ScenarioRow` shape the rail expects. The
          // rail tolerates missing optional fields.
          scenarios={(scenarios ?? []) as unknown as ScenarioRow[]}
          activeFusionId={presentationId ?? null}
          onSelect={(id) => navigate(`/presentation/${id}`)}
          collapsed={railCollapsed === 'closed'}
          onToggleCollapse={toggleRail}
        />
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4 md:flex-row md:items-start md:overflow-hidden">
          {/* Left column — fixed 420px on md+ */}
          <div className="flex flex-col gap-4 md:h-full md:w-[420px] md:min-w-[420px] md:overflow-y-auto md:pr-1">
            <SummaryCard text={description} loading={isLoading} />
            <ApplicationsCard applications={applications} />
            <ExecutionsCard operations={lastRunOperations} />
            <InfoCard
              modules={totalModules}
              hasNotifications={hasNotifications}
              status={status === 'On' ? 'Active' : status === 'Off' ? 'Inactive' : status || '—'}
              tags={tags
                .map((t) => ({
                  id: typeof t.id === 'number' ? t.id : null,
                  title: t.title ?? t.name ?? '',
                  color: t.color ?? null,
                  icon: t.icon ?? null,
                }))
                .filter(
                  (
                    t,
                  ): t is {
                    id: number;
                    title: string;
                    color: string | null;
                    icon: string | null;
                  } => t.id !== null && t.title.length > 0,
                )}
              onRemoveTag={removeTag}
              schedule={schedule || 'No schedule'}
              folder={folder}
              modifiedAt={modifiedAt}
              modifiedBy={modifiedBy}
            />
          </div>

          {/* Right column — KPI table on top, screenshot below.
              Mirrors the legacy `wf-audit/.../presenationItem` layout
              where the per-scenario KPI rollup (Hours Saved / Value /
              Successful Executions % / Operations %) sits above the
              process diagram. Today the engine doesn't expose the
              per-scenario KPI aggregate, so the table renders headers
              + a "No KPI data available" empty row — the layout slots
              in correctly so swapping to live data later is a
              one-prop change once `/v1/kpi-values` lands. */}
          <div className="flex flex-1 flex-col gap-4 md:h-full md:overflow-y-auto">
            <PresentationKpiTable rows={kpiTableRows} />
            {/* Same zoom/pan/download diagram the scenario detail
                modal uses (PRs #137 + #138). `60 vh` matches the
                modal's default; combined with the page's viewport
                clamp above + the right column's `overflow-y-auto`,
                the KPI table + diagram together never push the page
                taller than the viewport. */}
            <ScenarioProcessDiagram
              link={screenshotLink || null}
              modifiedAt={modifiedAt || null}
              scenarioName={name || null}
              fusionId={presentationId ?? null}
              heightClass="h-[60vh] min-h-[320px]"
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>

      <FusionRequestSheet
        open={fusionRequestOpen}
        onOpenChange={setFusionRequestOpen}
        scenarioId={presentationId}
        scenarioName={name}
      />
      <ScenarioAiSummarySheet
        open={aiSummaryOpen}
        onOpenChange={setAiSummaryOpen}
        title={`Technical Steps for "${name}"`}
        text={aiTechnicalSummary || 'No technical steps available.'}
      />
    </div>
  );
}

// ─── Cards ─────────────────────────────────────────────────────────────

function SectionCard({
  title,
  children,
  className,
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <section
      className={`rounded-lg border border-foreground/10 bg-foreground/[0.03] p-4 ${className ?? ''}`}
    >
      {title ? (
        <header className="mb-2 text-xs font-semibold uppercase tracking-wider text-foreground/55">
          {title}
        </header>
      ) : null}
      {children}
    </section>
  );
}

function SummaryCard({ text, loading }: { text: string; loading: boolean }): JSX.Element {
  const html = useMemo<string>(() => {
    if (!text) return '';
    marked.setOptions({ gfm: true, breaks: true });
    return marked.parse(text) as string;
  }, [text]);
  return (
    <SectionCard title="Executive Summary">
      {loading && !text ? (
        <div className="text-sm text-foreground/45">Loading summary…</div>
      ) : text ? (
        <article
          // Markdown comes from the scenario's description field which
          // is operator-authored (admin-side) — same trust model as the
          // AI summary sheet. Sanitisation isn't needed; the renderer
          // produces a fixed allowlist of HTML.
          className="prose prose-invert prose-sm max-w-none text-foreground/80 prose-headings:text-foreground prose-strong:text-foreground prose-a:text-[color:var(--brand-yellow)]"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <p className="text-sm text-foreground/50">No description provided.</p>
      )}
    </SectionCard>
  );
}

function ApplicationsCard({
  applications,
}: {
  applications: ReadonlyArray<{ name?: string; label?: string; type?: string }>;
}): JSX.Element {
  if (applications.length === 0) {
    return (
      <SectionCard title="Applications">
        <p className="text-sm text-foreground/50">No applications recorded.</p>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Applications">
      <ul className="flex flex-wrap gap-1.5">
        {applications.map((a, i) => {
          const label = a.label ?? a.name ?? a.type ?? 'Unknown';
          return (
            <li
              key={`${label}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-foreground/15 bg-foreground/5 px-2.5 py-1 text-xs text-foreground/85"
            >
              <Briefcase className="h-3 w-3 text-foreground/55" />
              {label}
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}

function ExecutionsCard({ operations }: { operations: number }): JSX.Element {
  // Legacy renders a real success/warning/error pie from the scenario's
  // KPI metrics array (`scenario.metrics.kpiMetrics`). The new engine
  // response doesn't carry the per-scenario metrics array yet — that's
  // a separate /v1/kpi-values/{scenarioId} call which we don't make on
  // this page today. Card shows a "single-slice" pie when ops > 0 so
  // the visual cue stays consistent + a graceful no-data state when
  // we're still pre-KPI for this scenario. Full pie returns when the
  // metrics summary lands as a follow-up.
  const hasData = operations > 0;
  const chartData = hasData
    ? [{ name: 'Operations', value: operations, color: '#22c55e' }]
    : [{ name: 'No Data', value: 1, color: '#475569' }];

  return (
    <SectionCard title="Executions">
      <div className="flex items-center gap-4">
        <div className="relative h-[103px] w-[120px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={48}
                paddingAngle={hasData ? 2 : 0}
                dataKey="value"
                strokeWidth={0}
              >
                {chartData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: '#0f172a',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#e5e7eb',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span
              className={`text-base font-semibold ${hasData ? 'text-foreground' : 'text-foreground/40'}`}
            >
              {hasData ? operations.toLocaleString() : '0'}
            </span>
            <span className={`text-xs ${hasData ? 'text-foreground/70' : 'text-foreground/40'}`}>
              {hasData ? 'Operations' : 'No Data'}
            </span>
          </div>
        </div>
        <div className="text-xs leading-snug text-foreground/55">
          From the most recent successful run. Full per-day metrics chart will land alongside the
          per-scenario KPI fetch (TODO — wire `/v1/kpi-values` once paged).
        </div>
      </div>
    </SectionCard>
  );
}

interface InfoCardTag {
  id: number;
  title: string;
  color: string | null;
  icon: string | null;
}

function InfoCard({
  modules,
  hasNotifications,
  status,
  tags,
  onRemoveTag,
  schedule,
  folder,
  modifiedAt,
  modifiedBy,
}: {
  modules: number;
  hasNotifications: boolean;
  status: string;
  tags: ReadonlyArray<InfoCardTag>;
  /** Called when the operator clicks the `X` on a tag chip. Triggers
   *  a PUT /v1/scenarios/{id} with the current tag set minus the
   *  clicked one. Mirrors legacy inline-remove behaviour where each
   *  chip carried its own dismiss affordance. */
  onRemoveTag: (tagId: number) => void;
  schedule: string;
  folder: string;
  modifiedAt: string;
  modifiedBy: string;
}): JSX.Element {
  return (
    <SectionCard title="Info">
      <table className="w-full text-sm">
        <tbody>
          <Row icon={Zap} label="Modules" value={String(modules)} />
          <Row
            icon={CheckCircle2}
            label="Has Notifications"
            value={hasNotifications ? 'Yes' : 'No'}
          />
          <Row icon={Settings} label="Status" value={status} />
          <tr className="border-b border-foreground/5">
            <td className="py-1.5 pr-2 align-top">
              <span className="inline-flex items-center gap-1.5 text-foreground/55">
                <Tag className="h-3 w-3" /> Tags
              </span>
            </td>
            <td className="py-1.5 text-right text-foreground/85">
              {tags.length > 0 ? (
                <div className="flex flex-wrap items-center justify-end gap-1">
                  {tags.map((t) => (
                    <span key={t.id} className="inline-flex items-center gap-0.5">
                      <TagChip tag={t} size="sm" />
                      <button
                        type="button"
                        onClick={() => onRemoveTag(t.id)}
                        className="rounded p-0.5 text-foreground/40 hover:bg-foreground/10 hover:text-foreground/80"
                        aria-label={`Remove tag ${t.title}`}
                        title={`Remove tag ${t.title}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                '—'
              )}
            </td>
          </tr>
          <Row icon={Calendar} label="Schedule" value={schedule} />
          <Row icon={Folder} label="Folder" value={folder || '—'} />
          <Row icon={Calendar} label="Last Modified" value={formatDateTime(modifiedAt) || '—'} />
          <Row icon={User} label="Last Modified By" value={modifiedBy || '—'} last />
        </tbody>
      </table>
    </SectionCard>
  );
}

function Row({
  icon: Icon,
  label,
  value,
  last,
}: {
  icon: typeof Zap;
  label: string;
  value: string;
  last?: boolean;
}): JSX.Element {
  return (
    <tr className={last ? '' : 'border-b border-foreground/5'}>
      <td className="py-1.5 pr-2 align-top">
        <span className="inline-flex items-center gap-1.5 text-foreground/55">
          <Icon className="h-3 w-3" /> {label}
        </span>
      </td>
      <td className="py-1.5 text-right text-foreground/85" title={value}>
        <span className="line-clamp-2">{value}</span>
      </td>
    </tr>
  );
}
